import React, { useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart, BarChart, Bar, Legend } from "recharts";

// ─── SKILLS CONFIG ───────────────────────────────────────────────────────────

const SKILLS = {
  reason: { name: "Reasoning", short: "Reason", color: "#2563EB" },
  construct_arguments: { name: "Construct Arguments", short: "Arguments", color: "#059669" },
  precision: { name: "Precision", short: "Precision", color: "#D97706" },
  model_analyze: { name: "Model & Analyze", short: "Model", color: "#7C3AED" },
  structure: { name: "Structure Recognition", short: "Structure", color: "#0891B2" },
  communicate: { name: "Communication", short: "Comm.", color: "#DB2777" },
  grit: { name: "Grit", short: "Grit", color: "#DC2626" },
  flexible: { name: "Flexible Thinking", short: "Flexible", color: "#65A30D" },
};

const VISIBLE_SKILLS = ["reason", "construct_arguments", "precision", "model_analyze", "communicate", "grit"];

// ─── STUDENT LONGITUDINAL DATA ───────────────────────────────────────────────

const STUDENT = {
  id: "STU-4419", name: "Student 4419", grade: 4, teacher: "Mr. Okonkwo", class: "Room 12",
  challenges: [
    { id: "ch1", name: "Number Neighborhoods", date: "Sep 2025", month: "Sep",
      scores: { reason: 52, construct_arguments: 35, precision: 75, model_analyze: 40, communicate: 67, grit: 60, structure: 80, flexible: 0 }},
    { id: "ch2", name: "Shape Shifters", date: "Oct 2025", month: "Oct",
      scores: { reason: 58, construct_arguments: 40, precision: 78, model_analyze: 45, communicate: 67, grit: 60, structure: 85, flexible: 50 }},
    { id: "ch3", name: "Fraction Action", date: "Nov 2025", month: "Nov",
      scores: { reason: 60, construct_arguments: 42, precision: 82, model_analyze: 48, communicate: 75, grit: 80, structure: 80, flexible: 50 }},
    { id: "ch4", name: "Animals Big & Small", date: "Jan 2026", month: "Jan",
      scores: { reason: 65, construct_arguments: 50, precision: 75, model_analyze: 45, communicate: 100, grit: 80, structure: 100, flexible: 0 }},
    { id: "ch5", name: "Data Detectives", date: "Feb 2026", month: "Feb",
      scores: { reason: 68, construct_arguments: 55, precision: 85, model_analyze: 55, communicate: 100, grit: 80, structure: 90, flexible: 50 }},
    { id: "ch6", name: "Measurement Mania", date: "Mar 2026", month: "Mar",
      scores: { reason: 72, construct_arguments: 58, precision: 88, model_analyze: 60, communicate: 100, grit: 100, structure: 95, flexible: 100 }},
  ],
};

// ─── CLASS LONGITUDINAL DATA ─────────────────────────────────────────────────

const CLASS_DATA = {
  name: "Room 12 — Mr. Okonkwo", grade: 4, student_count: 24,
  periods: [
    { month: "Sep", reason: 48, construct_arguments: 38, precision: 65, model_analyze: 42, communicate: 60, grit: 55 },
    { month: "Oct", reason: 52, construct_arguments: 40, precision: 68, model_analyze: 44, communicate: 62, grit: 58 },
    { month: "Nov", reason: 55, construct_arguments: 43, precision: 72, model_analyze: 48, communicate: 65, grit: 62 },
    { month: "Jan", reason: 58, construct_arguments: 45, precision: 76, model_analyze: 47, communicate: 70, grit: 68 },
    { month: "Feb", reason: 62, construct_arguments: 50, precision: 80, model_analyze: 52, communicate: 72, grit: 72 },
    { month: "Mar", reason: 66, construct_arguments: 54, precision: 82, model_analyze: 56, communicate: 75, grit: 75 },
  ],
  proficiency_over_time: [
    { month: "Sep", exceeded: 2, met: 6, nearly: 10, did_not: 6 },
    { month: "Oct", exceeded: 2, met: 7, nearly: 10, did_not: 5 },
    { month: "Nov", exceeded: 3, met: 8, nearly: 9, did_not: 4 },
    { month: "Jan", exceeded: 3, met: 9, nearly: 8, did_not: 4 },
    { month: "Feb", exceeded: 4, met: 10, nearly: 7, did_not: 3 },
    { month: "Mar", exceeded: 5, met: 10, nearly: 6, did_not: 3 },
  ],
};

// ─── GRADE LONGITUDINAL DATA ─────────────────────────────────────────────────

const GRADE_DATA = {
  grade: 4, classes: 4, total_students: 96,
  class_comparison: [
    { name: "Room 12\nOkonkwo", reason: 66, construct_arguments: 54, precision: 82, model_analyze: 56 },
    { name: "Room 14\nPatel", reason: 62, construct_arguments: 60, precision: 78, model_analyze: 52 },
    { name: "Room 15\nReeves", reason: 55, construct_arguments: 45, precision: 70, model_analyze: 48 },
    { name: "Room 18\nChen", reason: 70, construct_arguments: 58, precision: 85, model_analyze: 62 },
  ],
  periods: [
    { month: "Sep", reason: 46, construct_arguments: 36, precision: 62, model_analyze: 40, communicate: 58, grit: 52 },
    { month: "Oct", reason: 50, construct_arguments: 40, precision: 66, model_analyze: 43, communicate: 60, grit: 55 },
    { month: "Nov", reason: 54, construct_arguments: 43, precision: 70, model_analyze: 46, communicate: 63, grit: 60 },
    { month: "Jan", reason: 57, construct_arguments: 46, precision: 74, model_analyze: 48, communicate: 66, grit: 64 },
    { month: "Feb", reason: 60, construct_arguments: 50, precision: 78, model_analyze: 52, communicate: 69, grit: 68 },
    { month: "Mar", reason: 64, construct_arguments: 53, precision: 80, model_analyze: 55, communicate: 72, grit: 72 },
  ],
};

// ─── SCHOOL LONGITUDINAL DATA ────────────────────────────────────────────────

const SCHOOL_DATA = {
  name: "Lincoln Elementary", total_students: 480, grades: [3, 4, 5, 6],
  grade_comparison: [
    { grade: "Grade 3", reason: 58, construct_arguments: 45, precision: 72, model_analyze: 48, students: 112 },
    { grade: "Grade 4", reason: 64, construct_arguments: 53, precision: 80, model_analyze: 55, students: 96 },
    { grade: "Grade 5", reason: 68, construct_arguments: 58, precision: 82, model_analyze: 60, students: 136 },
    { grade: "Grade 6", reason: 72, construct_arguments: 62, precision: 85, model_analyze: 65, students: 136 },
  ],
  periods: [
    { month: "Sep", reason: 48, construct_arguments: 38, precision: 64, model_analyze: 42 },
    { month: "Oct", reason: 52, construct_arguments: 42, precision: 68, model_analyze: 45 },
    { month: "Nov", reason: 55, construct_arguments: 44, precision: 71, model_analyze: 48 },
    { month: "Jan", reason: 58, construct_arguments: 47, precision: 74, model_analyze: 50 },
    { month: "Feb", reason: 62, construct_arguments: 51, precision: 78, model_analyze: 54 },
    { month: "Mar", reason: 65, construct_arguments: 54, precision: 80, model_analyze: 57 },
  ],
};

// ─── TEACHER GUIDANCE (TREND-AWARE) ──────────────────────────────────────────

const LONGITUDINAL_GUIDANCE = {
  student: [
    {
      priority: "high", skill: "construct_arguments", color: "#059669",
      title: "Construct Arguments: Improving but Still Below Proficiency",
      trend: "35% → 58% (+23 pts over 6 challenges)",
      insight: "This is the strongest growth trajectory of any skill for this student, rising from 35% to 58%. However, the growth rate is decelerating — the jump from Sep to Jan was +15 pts, but Jan to Mar added only +8. The student is approaching a ceiling created by the same pattern flagged in Animals Big and Small: correct conclusions with thin evidence. Without targeted intervention, this trajectory plateaus around 60%.",
      action: "The CER framework is working — keep it. But add a 'specificity check' step: before submitting, student must verify they referenced at least one specific number or data point from the problem. This addresses the deceleration directly.",
    },
    {
      priority: "high", skill: "flexible", color: "#65A30D",
      title: "Flexible Thinking: Volatile — No Consistent Growth",
      trend: "0% → 100% (but pattern is 0, 50, 50, 0, 50, 100)",
      insight: "This skill shows no stable trajectory. The student oscillates between 0% and 50-100%, suggesting the skill is context-dependent rather than internalized. When prompts explicitly invite alternative perspectives, the student can produce them. When prompts require the student to generate the questioning disposition independently (like Q3 in Animals Big and Small), they default to acceptance. This is a disposition gap, not a skill gap.",
      action: "Build the questioning habit outside of assessed work. Start each math class with a 'Challenge the Claim' warm-up where students must find an exception to a general statement. Track whether the volatility stabilizes over the next 2 challenges.",
    },
    {
      priority: "low", skill: "precision", color: "#D97706",
      title: "Precision: Strong and Stable",
      trend: "75% → 88% (+13 pts, consistent upward)",
      insight: "Precision dipped slightly in January (Animals Big and Small, unit omission issue) but recovered strongly. The unit-check protocol appears to be working. This skill is approaching the 'Exceeded' threshold and requires no further intervention.",
      action: "Maintain current protocols. Consider having this student mentor peers who are struggling with precision.",
    },
  ],
  class: [
    {
      priority: "high", skill: "construct_arguments", color: "#059669",
      title: "Class-Wide Argument Quality: Slow Growth Despite Intervention",
      trend: "38% → 54% (+16 pts, 6 months) — slowest growth of any skill",
      insight: "Constructing Arguments is growing at roughly half the rate of Precision (+16 vs +17 pts) despite receiving more instructional focus. The CER mini-lesson series moved the average from 45% to 54% (Jan-Mar), which is meaningful but still leaves 10 students below proficiency. The bottleneck is consistent: students describe conclusions but don't cite evidence. This is a class-wide pattern, not isolated to a few students.",
      action: "The current approach (CER framework) is necessary but not sufficient. Add a structural intervention: modify challenge submission requirements so that explanation questions literally cannot be submitted without a minimum character count. This forces elaboration at the point of production, not just during instruction.",
    },
    {
      priority: "high", skill: "model_analyze", color: "#7C3AED",
      title: "Model & Analyze: Only Skill Trending Below Growth Target",
      trend: "42% → 56% (+14 pts) — growth rate declined in last 2 months",
      insight: "Model & Analyze grew steadily Sep-Nov (+6 pts) then stalled Jan-Mar (+8 pts over 3 months vs. +6 pts over 2 months previously). Students can perform the mathematical operations but are not connecting them to real-world predictions. This suggests the challenge design may need to scaffold the connection more explicitly — students aren't making the inferential leap on their own.",
      action: "Add 'bridge prompts' between the computation and the prediction: after Q6 (comparison), before Q7 (prediction), insert a scaffolding prompt: 'The female weighs ___ more/less than the male. What might that mean about how much food they need?' This makes the inferential step visible.",
    },
  ],
  grade: [
    {
      priority: "high", color: "#DC2626",
      title: "Cross-Class Variance: Room 15 Significantly Below Grade Average",
      insight: "Room 15 (Reeves) trails the grade average by 8-10 points across all four core skills. This is not a student composition issue — diagnostic data shows similar entering profiles across all four classes. The gap widened from October onward, suggesting an instructional delivery issue rather than a curriculum issue. Room 12 (Okonkwo) and Room 18 (Chen) are outperforming, with Chen showing particular strength in Reasoning (+6 pts above grade average).",
      action: "Recommend structured peer observation: Reeves observes Okonkwo's and Chen's explanation question facilitation strategies. Consider grade-level PLC focused on argument quality instruction, using Chen's approach as a model.",
    },
    {
      priority: "medium", color: "#7C3AED",
      title: "Grade-Wide: Model & Analyze Underperforming Relative to Other Skills",
      insight: "Across all four classes, Model & Analyze is the weakest skill (55% grade average vs. 80% for Precision). This is not a teacher effect — all four classes show the same pattern. This suggests a curriculum-level issue: the challenges may not be scaffolding the real-world connection step sufficiently.",
      action: "Curriculum team review: examine whether challenge prompts adequately bridge mathematical operations to real-world reasoning. Consider adding explicit modeling steps in challenge design for the next unit.",
    },
  ],
  school: [
    {
      priority: "high", color: "#2563EB",
      title: "School-Wide Growth: On Track but Argument Quality Lags Across All Grades",
      insight: "Lincoln Elementary shows consistent growth across all four assessed grades (3-6). Precision leads at 80% school average, indicating strong procedural instruction. However, Constructing Arguments trails at 54% — the lowest skill across every grade level. This is a school-wide pattern, not grade-specific, suggesting a systemic gap in how explanation and evidence-based reasoning are taught.",
      action: "School-wide PD recommendation: implement CER (Claim-Evidence-Reasoning) framework training for all math teachers in grades 3-6. Establish a common language and expectation for mathematical argumentation that builds progressively across grade levels. Track this as the primary school improvement metric for the remainder of the year.",
    },
    {
      priority: "medium", color: "#059669",
      title: "Grade Progression: Skills Transfer Is Working for Precision but Not Arguments",
      insight: "Precision shows healthy grade-over-grade progression (72% → 80% → 82% → 85%). Students are building on prior precision habits as they advance. Constructing Arguments shows a similar progression (45% → 53% → 58% → 62%) but at a much lower baseline. The gap between Precision and Arguments stays roughly constant across grades (~25-27 pts), meaning the issue is not being resolved by maturation or experience alone.",
      action: "This validates that Argument quality requires explicit instruction, not just exposure. The progressive improvement shows students can grow in this skill — the school needs to accelerate the rate by making argumentation a formal curriculum strand, not just an embedded skill.",
    },
  ],
};

// ─── UTILITY ─────────────────────────────────────────────────────────────────

const proficiencyLine = 70;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div style={{
      background: "#111827", padding: "10px 14px", borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)", maxWidth: "220px",
    }}>
      <div style={{ fontSize: "11px", fontWeight: 700, color: "#9CA3AF", marginBottom: "6px" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "2px" }}>
          <span style={{ fontSize: "11px", color: p.color, fontWeight: 600 }}>{p.name}</span>
          <span style={{ fontSize: "11px", color: "#F9FAFB", fontWeight: 700 }}>{p.value}%</span>
        </div>
      ))}
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function LongitudinalGrowth() {
  const [level, setLevel] = useState("student");
  const [selectedSkills, setSelectedSkills] = useState(["reason", "construct_arguments", "precision", "model_analyze"]);
  const [showGuidance, setShowGuidance] = useState(true);
  const [showJson, setShowJson] = useState(false);

  const toggleSkill = (sk) => {
    setSelectedSkills(prev =>
      prev.includes(sk) ? prev.filter(s => s !== sk) : [...prev, sk]
    );
  };

  const tab = (label, val) => ({
    padding: "7px 16px", borderRadius: "7px", border: "none", cursor: "pointer",
    fontSize: "12px", fontWeight: 600, transition: "all 0.15s",
    background: level === val ? "#111827" : "transparent",
    color: level === val ? "#FFF" : "#6B7280",
  });

  const chartData = level === "student"
    ? STUDENT.challenges.map(c => ({ name: c.month, label: c.name, ...c.scores }))
    : level === "class" ? CLASS_DATA.periods.map(p => ({ name: p.month, ...p }))
    : level === "grade" ? GRADE_DATA.periods.map(p => ({ name: p.month, ...p }))
    : SCHOOL_DATA.periods.map(p => ({ name: p.month, ...p }));

  const guidance = LONGITUDINAL_GUIDANCE[level] || [];

  // Growth velocity calc for student
  const calcVelocity = (sk) => {
    if (level !== "student") return null;
    const vals = STUDENT.challenges.map(c => c.scores[sk]);
    if (vals.length < 2) return null;
    const first = vals[0], last = vals[vals.length - 1];
    const delta = last - first;
    const recentDelta = last - vals[vals.length - 2];
    return { total: delta, recent: recentDelta, current: last, start: first };
  };

  // JSON output
  const jsonOutput = {
    level,
    entity: level === "student" ? { id: STUDENT.id, grade: STUDENT.grade }
      : level === "class" ? { name: CLASS_DATA.name, students: CLASS_DATA.student_count }
      : level === "grade" ? { grade: GRADE_DATA.grade, classes: GRADE_DATA.classes, students: GRADE_DATA.total_students }
      : { name: SCHOOL_DATA.name, students: SCHOOL_DATA.total_students },
    time_range: "September 2025 — March 2026",
    growth_trajectories: selectedSkills.map(sk => {
      const data = chartData.map(d => ({ period: d.name, score_pct: d[sk] }));
      const v = calcVelocity(sk);
      return {
        skill_id: sk, skill_name: SKILLS[sk].name,
        data_points: data,
        total_growth: v ? `+${v.total}` : `+${data[data.length-1][sk] - data[0][sk] || "—"}`,
        current_pct: data[data.length - 1].score_pct,
        proficiency_status: data[data.length - 1].score_pct >= proficiencyLine ? "at_or_above" : "below",
      };
    }),
    teacher_guidance: guidance,
  };

  return (
    <div style={{
      fontFamily: "'Instrument Sans', 'DM Sans', 'Segoe UI', system-ui, sans-serif",
      background: "#F8F9FB", minHeight: "100vh",
    }}>
      {/* Header */}
      <div style={{ background: "#111827", padding: "24px 28px 18px", borderBottom: "3px solid #7C3AED" }}>
        <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#A78BFA", marginBottom: "6px" }}>
          Mindsets Intelligence Engine — Longitudinal Growth
        </div>
        <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#F9FAFB" }}>
          Future-Ready Skills Growth Over Time
        </h1>
        <div style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "4px" }}>
          September 2025 — March 2026 · 6 of 12 Challenges Completed
        </div>
      </div>

      {/* Level selector */}
      <div style={{
        padding: "12px 28px", display: "flex", alignItems: "center", gap: "12px",
        borderBottom: "1px solid #E5E7EB", background: "#FFF", flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", gap: "3px", background: "#F3F4F6", padding: "3px", borderRadius: "9px" }}>
          <button style={tab("Student", "student")} onClick={() => setLevel("student")}>Student</button>
          <button style={tab("Class", "class")} onClick={() => setLevel("class")}>Class</button>
          <button style={tab("Grade", "grade")} onClick={() => setLevel("grade")}>Grade Level</button>
          <button style={tab("School", "school")} onClick={() => setLevel("school")}>School</button>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          <button onClick={() => setShowGuidance(!showGuidance)} style={{
            padding: "6px 14px", borderRadius: "6px", border: "1px solid #FDE68A",
            background: showGuidance ? "#FFFBEB" : "#FFF", color: showGuidance ? "#92400E" : "#9CA3AF",
            fontSize: "11px", fontWeight: 600, cursor: "pointer",
          }}>&#9881; Guidance {showGuidance ? "ON" : "OFF"}</button>
          <button onClick={() => setShowJson(!showJson)} style={{
            padding: "6px 14px", borderRadius: "6px", border: "1px solid #D1D5DB",
            background: showJson ? "#111827" : "#FFF", color: showJson ? "#FFF" : "#374151",
            fontSize: "11px", fontWeight: 600, cursor: "pointer",
          }}>{showJson ? "◉" : "○"} JSON</button>
        </div>
      </div>

      <div style={{ padding: "20px 28px", maxWidth: "1000px" }}>
        {/* Context bar */}
        <div style={{
          padding: "14px 18px", background: "#FFF", borderRadius: "10px",
          border: "1px solid #E5E7EB", marginBottom: "16px",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px",
        }}>
          {level === "student" && (
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{STUDENT.id}</div>
              <div style={{ fontSize: "12px", color: "#6B7280" }}>Grade {STUDENT.grade} · {STUDENT.teacher} — {STUDENT.class} · 6 of 12 challenges completed</div>
            </div>
          )}
          {level === "class" && (
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{CLASS_DATA.name}</div>
              <div style={{ fontSize: "12px", color: "#6B7280" }}>{CLASS_DATA.student_count} students · Grade {CLASS_DATA.grade}</div>
            </div>
          )}
          {level === "grade" && (
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>Grade {GRADE_DATA.grade}</div>
              <div style={{ fontSize: "12px", color: "#6B7280" }}>{GRADE_DATA.classes} classes · {GRADE_DATA.total_students} students</div>
            </div>
          )}
          {level === "school" && (
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{SCHOOL_DATA.name}</div>
              <div style={{ fontSize: "12px", color: "#6B7280" }}>Grades {SCHOOL_DATA.grades.join(", ")} · {SCHOOL_DATA.total_students} students</div>
            </div>
          )}
          <div style={{ fontSize: "10px", padding: "4px 10px", borderRadius: "5px", background: "#F3F4F6", color: "#6B7280", fontWeight: 600 }}>
            Proficiency threshold: {proficiencyLine}%
          </div>
        </div>

        {/* Skill filter */}
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
          {VISIBLE_SKILLS.map(sk => (
            <button key={sk} onClick={() => toggleSkill(sk)} style={{
              padding: "5px 12px", borderRadius: "6px", border: `1.5px solid ${SKILLS[sk].color}`,
              background: selectedSkills.includes(sk) ? `${SKILLS[sk].color}14` : "#FFF",
              color: selectedSkills.includes(sk) ? SKILLS[sk].color : "#9CA3AF",
              fontSize: "11px", fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
              opacity: selectedSkills.includes(sk) ? 1 : 0.5,
            }}>
              {SKILLS[sk].short}
            </button>
          ))}
        </div>

        {/* ── MAIN GROWTH CHART ── */}
        <div style={{
          background: "#FFF", borderRadius: "10px", border: "1px solid #E5E7EB",
          padding: "20px 16px 10px 0", marginBottom: "16px",
        }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px", paddingLeft: "20px" }}>
            Skill Growth Trajectories (% of Max Score)
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B7280" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={v => `${v}%`} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={proficiencyLine} stroke="#9CA3AF" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: "Proficient", position: "right", fontSize: 10, fill: "#9CA3AF" }} />
              {selectedSkills.map(sk => (
                <Line key={sk} type="monotone" dataKey={sk} name={SKILLS[sk].short}
                  stroke={SKILLS[sk].color} strokeWidth={2.5} dot={{ r: 4, fill: SKILLS[sk].color }}
                  activeDot={{ r: 6 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* ── GROWTH VELOCITY (Student level) ── */}
        {level === "student" && (
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "8px", marginBottom: "16px",
          }}>
            {selectedSkills.map(sk => {
              const v = calcVelocity(sk);
              if (!v) return null;
              const atProf = v.current >= proficiencyLine;
              const accelerating = v.recent > (v.total / STUDENT.challenges.length);
              return (
                <div key={sk} style={{
                  padding: "14px 16px", background: "#FFF", borderRadius: "8px",
                  border: "1px solid #E5E7EB", borderLeft: `4px solid ${SKILLS[sk].color}`,
                }}>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#111827", marginBottom: "6px" }}>{SKILLS[sk].name}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: "22px", fontWeight: 800, color: SKILLS[sk].color }}>{v.current}%</span>
                    <span style={{
                      fontSize: "12px", fontWeight: 700,
                      color: v.total > 0 ? "#059669" : v.total < 0 ? "#DC2626" : "#6B7280",
                    }}>+{v.total} pts</span>
                  </div>
                  <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                    <span style={{
                      fontSize: "9px", padding: "2px 6px", borderRadius: "3px", fontWeight: 600,
                      background: atProf ? "#ECFDF5" : "#FEF2F2",
                      color: atProf ? "#065F46" : "#991B1B",
                    }}>{atProf ? "PROFICIENT" : "BELOW"}</span>
                    <span style={{
                      fontSize: "9px", padding: "2px 6px", borderRadius: "3px", fontWeight: 600,
                      background: accelerating ? "#EFF6FF" : "#FFFBEB",
                      color: accelerating ? "#1E40AF" : "#92400E",
                    }}>{accelerating ? "ACCELERATING" : "DECELERATING"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── PROFICIENCY MOVEMENT (Class level) ── */}
        {level === "class" && (
          <div style={{
            background: "#FFF", borderRadius: "10px", border: "1px solid #E5E7EB",
            padding: "20px 16px 10px 0", marginBottom: "16px",
          }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px", paddingLeft: "20px" }}>
              Proficiency Distribution Over Time
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={CLASS_DATA.proficiency_over_time} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B7280" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} />
                <Tooltip />
                <Area type="monotone" dataKey="exceeded" stackId="1" fill="#059669" stroke="#059669" name="Exceeded" />
                <Area type="monotone" dataKey="met" stackId="1" fill="#2563EB" stroke="#2563EB" name="Met" />
                <Area type="monotone" dataKey="nearly" stackId="1" fill="#D97706" stroke="#D97706" name="Nearly Met" />
                <Area type="monotone" dataKey="did_not" stackId="1" fill="#DC2626" stroke="#DC2626" name="Did Not Meet" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── CLASS COMPARISON (Grade level) ── */}
        {level === "grade" && (
          <div style={{
            background: "#FFF", borderRadius: "10px", border: "1px solid #E5E7EB",
            padding: "20px 16px 10px 0", marginBottom: "16px",
          }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px", paddingLeft: "20px" }}>
              Current Skill Averages by Class (March 2026)
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={GRADE_DATA.class_comparison} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} interval={0} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={v => `${v}%`} />
                <Tooltip />
                <ReferenceLine y={proficiencyLine} stroke="#9CA3AF" strokeDasharray="6 4" />
                <Bar dataKey="reason" fill="#2563EB" name="Reasoning" radius={[3,3,0,0]} />
                <Bar dataKey="construct_arguments" fill="#059669" name="Arguments" radius={[3,3,0,0]} />
                <Bar dataKey="precision" fill="#D97706" name="Precision" radius={[3,3,0,0]} />
                <Bar dataKey="model_analyze" fill="#7C3AED" name="Model" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── GRADE COMPARISON (School level) ── */}
        {level === "school" && (
          <div style={{
            background: "#FFF", borderRadius: "10px", border: "1px solid #E5E7EB",
            padding: "20px 16px 10px 0", marginBottom: "16px",
          }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px", paddingLeft: "20px" }}>
              Current Skill Averages by Grade (March 2026)
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={SCHOOL_DATA.grade_comparison} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="grade" tick={{ fontSize: 11, fill: "#6B7280" }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={v => `${v}%`} />
                <Tooltip />
                <ReferenceLine y={proficiencyLine} stroke="#9CA3AF" strokeDasharray="6 4" />
                <Bar dataKey="reason" fill="#2563EB" name="Reasoning" radius={[3,3,0,0]} />
                <Bar dataKey="construct_arguments" fill="#059669" name="Arguments" radius={[3,3,0,0]} />
                <Bar dataKey="precision" fill="#D97706" name="Precision" radius={[3,3,0,0]} />
                <Bar dataKey="model_analyze" fill="#7C3AED" name="Model" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── TEACHER GUIDANCE ── */}
        {showGuidance && guidance.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>
              Trend-Aware Teacher Guidance
              {level === "grade" && " — Grade Level"}{level === "school" && " — School Level"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {guidance.map((g, i) => (
                <div key={i} style={{
                  padding: "16px 18px", background: "#FFFBEB", borderRadius: "10px",
                  border: "1px solid #FDE68A", borderLeft: `4px solid ${g.color || "#D97706"}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <span style={{ fontSize: "14px" }}>&#9881;</span>
                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#78350F", flex: 1 }}>{g.title}</span>
                    <span style={{
                      fontSize: "9px", fontWeight: 700, padding: "2px 8px", borderRadius: "99px",
                      background: g.priority === "high" ? "#FEE2E2" : g.priority === "medium" ? "#FEF3C7" : "#D1FAE5",
                      color: g.priority === "high" ? "#991B1B" : g.priority === "medium" ? "#92400E" : "#065F46",
                      textTransform: "uppercase",
                    }}>{g.priority}</span>
                  </div>
                  {g.trend && (
                    <div style={{
                      fontSize: "11px", fontWeight: 700, color: "#92400E", marginBottom: "6px",
                      padding: "4px 10px", background: "#FEF3C7", borderRadius: "4px", display: "inline-block",
                    }}>
                      Trend: {g.trend}
                    </div>
                  )}
                  <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#451A03", lineHeight: 1.65 }}>{g.insight}</p>
                  <div style={{
                    marginTop: "10px", padding: "10px 14px", borderRadius: "6px",
                    background: "#FFFDE7", border: "1px dashed #D97706",
                  }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: "#B45309", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "3px" }}>Recommended Action</div>
                    <p style={{ margin: 0, fontSize: "12px", color: "#78350F", lineHeight: 1.6, fontWeight: 500 }}>{g.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* JSON */}
        {showJson && (
          <div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "6px" }}>
              Longitudinal API Response — {level} level
            </div>
            <pre style={{
              background: "#111827", color: "#D1D5DB", padding: "18px",
              borderRadius: "10px", fontSize: "11px", lineHeight: 1.5,
              overflow: "auto", maxHeight: "500px", margin: 0,
              fontFamily: "'SF Mono', 'Fira Code', monospace",
            }}>
              {JSON.stringify(jsonOutput, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
